import { graphql, PageProps, Link } from 'gatsby';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import BaseLayout from '../layouts/base';
import { GetArtikelDetailDataQuery } from '../graphql-types';
import { BtnPrimary, BtnLinkPrimary } from '../components/scaffolds';
import { ArticleItem } from '../components/article';
import { Helmet } from 'react-helmet';
import Img, { FluidObject } from 'gatsby-image';
import React from 'react';
import Partner from '../components/partner';

import { INLINES, BLOCKS, MARKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import { DateTime } from 'luxon';

function handleChange(e: any) {
  e.preventDefault();
}

const BlockList = ({ children }) => (
  <ul
    tw="grid grid-cols-1 lg:grid-cols-2 mt-4 list-none ml-0"
    css={css`
      li {
        margin: 0;
      }
    `}
  >
    {children}
  </ul>
);
const H4 = tw.h4`font-gilroy`;
const blockOptions = {
  renderMark: {
    [MARKS.CODE]: code => {
      return <div dangerouslySetInnerHTML={{ __html: code }}></div>;
    },
  },
  renderNode: {
    [BLOCKS.UL_LIST]: (_, children) => <BlockList>{children}</BlockList>,
    [INLINES.HYPERLINK]: (node, children) => {
      const url = node.data.uri;
      return (
        <BtnPrimary
          tw="w-full text-center my-3 flex items-center "
          className="gtm-call-button"
          href={url}
          target="__blank"
        >
          {children}
        </BtnPrimary>
      );
    },
  },
};

const descriptionOptions = {
  renderMark: {
    [MARKS.CODE]: children => {
      if (children.includes('$$')) {
        const child = children.split('$$');
        const full = child[2] === 'full';
        return (
          <BtnPrimary
            tw="text-center"
            css={css`
              ${full ? tw`w-full my-3 flex items-center` : tw`mx-1`}
            `}
            className="gtm-call-button"
            href={`https://` + child[1]}
            target="__blank"
          >
            {child[0]}
          </BtnPrimary>
        );
      } else {
        return <div dangerouslySetInnerHTML={{ __html: children }}></div>;
      }
    },
  },
  renderNode: {
    [BLOCKS.HEADING_4]: (_, children) => <H4>{children}</H4>,
    [INLINES.HYPERLINK]: (node, children) => {
      const url = node.data.uri;
      return (
        <a tw="dark:text-yellow-bright dark:visited:text-yellow-bright" href={url} target="__blank">
          {children}
        </a>
      );
    },
  },
};

const Breadcrumbs: React.FC<{
  style?: React.CSSProperties;
  className?: string;
  data: string[];
}> = ({ style, className, data }) => (
  <div style={style} className={className} tw="flex dark:text-white text-b2">
    {data.join(' > ')}
  </div>
);

function generateRoute(category) {
  if (category === 'Perizinan Berusaha') {
    return 'category/perizinan-berusaha';
  } else if (category === 'Badan Usaha') {
    return 'category/badan-usaha';
  } else if (category === 'Pajak Perusahaan') {
    return 'category/pajak-perusahaan';
  } else {
    return 'artikel';
  }
}

const ArtikelDetail: React.FC<PageProps<GetArtikelDetailDataQuery>> = ({ data, location }) => {
  const pageData = data.allContentfulArtikel.edges[0].node;
  const shortDescription = pageData?.shortDescription
    ? JSON.parse(pageData?.shortDescription?.raw)
    : null;
  const description = pageData?.description ? JSON.parse(pageData?.description?.raw) : null;
  const summary = pageData?.summary ? JSON.parse(pageData?.summary?.raw) : null;
  const recommendation = pageData?.recommendation
    ? JSON.parse(pageData?.recommendation?.raw)
    : null;

  const logo = data?.logo?.childrenImageSharp[0]?.fluid;

  // SEO data
  const siteMeta = data.site?.siteMetadata;
  const canonical = `${siteMeta.url}${location.pathname}`;

  // let tagProduct = 'product-item-container';
  // if (typeof window !== 'undefined') {
  //   if (window.innerWidth < 1024) {
  //     tagProduct = 'paket-express';
  //   }
  // }

  const relatedArticles = pageData.relatedArticle;

  const tags = pageData.tags;

  const breadcrumbs = ['Artikel', pageData?.category, pageData?.title];

  const desc = shortDescription ? shortDescription.content[0].content[0].value : pageData?.title;

  const seo = pageData?.seo;

  const imageUrl = pageData?.img?.file?.url?.replace(/^\/\//, 'https://');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          ...breadcrumbs.map((b, i) => {
            return {
              '@type': 'ListItem',
              position: i + 1,
              name: b,
              item:
                i === 0
                  ? `${siteMeta.url}/artikel`
                  : i === 1
                  ? `${siteMeta.url}/${generateRoute(b)}`
                  : `${siteMeta.url}/${pageData?.uid}/`,
            };
          }),
        ],
      },
      {
        '@type': 'NewsArticle',
        author: [
          {
            '@type': 'Person',
            name: seo?.author,
          },
        ],
        datePublished: seo?.updatedAt || '2021-03-22T05:34:36+00:00',
        headline: seo?.title || `${pageData?.title} - Easybiz`,
        image: imageUrl,
        publisher: {
          '@type': 'Organization',
          name: 'Easybiz',
          logo: {
            '@type': 'ImageObject',
            url: siteMeta?.logo,
          },
        },
      },
    ],
  };

  return (
    <BaseLayout logo={logo} pathname={location.pathname} fixedHeader={true}>
      <Helmet>
        <title>{seo?.title || `${pageData?.title} - Easybiz`}</title>
        <link rel="amphtml" href={canonical + '/amp'}></link>
        <meta name="description" content={seo?.description?.description || desc} />
        <meta name="keywords" content={seo?.keyword} />
        <meta
          name="robots"
          content={
            seo?.robots ||
            'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
          }
        />
        <link rel="canonical" href={canonical} />
        <meta name="author" content={seo?.author} />
        <meta name="publisher" content={seo?.publisher} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:height" content={'300'} />
        <meta property="og:locale" content={seo?.oglocale || 'en_US'} />
        <meta property="og:type" content={seo?.ogtype || 'article'} />
        <meta property="og:title" content={seo?.title || `${pageData?.title} - Easybiz`} />
        <meta property="og:description" content={seo?.description?.description || desc} />
        <meta property="og:url" content={`${siteMeta.url}/${pageData?.uid}/`} />
        <meta property="og:site_name" content="Easybiz" />
        <meta
          property="article:published_time"
          content={pageData?.createdAt || '2021-03-22T05:34:36+00:00'}
        />
        <meta
          property="article:modified_time"
          content={pageData?.updatedAt || '2021-03-22T05:34:36+00:00'}
        />
        <meta property="article:expiration_time" content="" />
        <meta property="profile:username" content={pageData?.author} />
        <meta property="article:section" content={pageData?.category} />
        {pageData?.tags?.map((tag, i) => {
          return <meta key={`meta-tag-${i}`} property="article:tag" content={tag} />;
        })}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={`${siteMeta.url}/${pageData?.uid}/`} />
        <meta name="twitter:creator" content={seo?.author} />
        <meta name="twitter:title" content={seo?.title || `${pageData?.title} - Easybiz`} />
        <meta name="twitter:description" content={seo?.description?.description || desc} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:image:alt" content={seo?.title || `${pageData?.title} - Easybiz`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd, undefined, 4)}</script>
      </Helmet>
      <section
        tw="bg-white dark:bg-black-200"
        css={css`
          -moz-user-select: none; /* firefox */
          -webkit-user-select: none; /* Safari */
          -ms-user-select: none; /* IE*/
          user-select: none; /* Standard syntax */
        `}
        onCut={handleChange}
        onCopy={handleChange}
        onPaste={handleChange}
      >
        <div tw="container">
          <div tw="mx-0 lg:mx-52 pt-24 pb-12 grid grid-cols-1 lg:grid-cols-3 lg:gap-4">
            <div tw="lg:col-span-2">
              <Breadcrumbs tw="mx-4 lg:mx-0" data={breadcrumbs}></Breadcrumbs>
              <div tw="my-4 mx-4 lg:mx-0">
                {pageData.img?.fluid ? (
                  <Img
                    alt={pageData?.imageAlt || pageData?.title || pageData?.uid}
                    tw="flex-none mr-4 rounded-lg mb-6"
                    fluid={pageData.img?.fluid as FluidObject}
                    css={css`
                      height: 330px;
                      width: 100%;
                      @media (max-width: 1024px) {
                        height: 200px;
                        width: 100%;
                      }
                    `}
                  />
                ) : (
                  <div
                    tw="flex-none mr-4 rounded-lg mb-6 bg-black-500 dark:bg-black-300"
                    css={css`
                      height: 330px;
                      width: 100%;
                      @media (max-width: 1024px) {
                        height: 200px;
                        width: 100%;
                      }
                    `}
                  ></div>
                )}
                <div tw="mt-4">
                  <div tw="text-b3 py-1 px-3 rounded-full bg-blue-sky w-max my-4">
                    <span>{pageData.category}</span>
                  </div>
                  <div tw="mt-4">
                    <h1 tw="text-h3 dark:text-white mb-2 font-extrabold leading-8">
                      {pageData.title}
                    </h1>
                  </div>
                  <div>
                    <span tw="text-b3 text-black-300 dark:text-black-700">
                      Published on{' '}
                      {DateTime.fromJSDate(new Date(pageData.date)).toFormat('dd MMMM yyyy', {
                        locale: 'id',
                      })}{' '}
                      • {pageData.info}
                    </span>
                  </div>
                  <div tw="mt-2 flex text-b3 text-black-300 dark:text-black-700 items-center mb-4">
                    <div
                      className="user-img"
                      tw="bg-black-500 rounded-full mr-2"
                      css={css`
                        height: 24px;
                        width: 24px;
                      `}
                    ></div>
                    <span>by {pageData.author} </span>
                  </div>
                </div>
              </div>

              <div tw="mb-6 mx-4 lg:mx-0 text-black-300 text-b0 dark:text-white font-medium">
                {documentToReactComponents(shortDescription)}
              </div>

              <div
                tw="bg-blue-sky p-4 text-b1 my-8"
                css={css`
                  ${!summary ? tw`hidden` : ''}
                `}
              >
                <div tw="mb-3">Ringkasan:</div>
                {documentToReactComponents(summary, blockOptions)}
                <div tw="mt-4">
                  <BtnLinkPrimary
                    tw="w-full text-center flex items-center justify-center"
                    className="gtm-call-button"
                    to={pageData?.summaryButtonLink || '/hubungi-kami-via-whatsapp'}
                    target={pageData?.summaryButtonLink ? null : '__blank'}
                  >
                    {pageData?.summaryButtonText || 'Hubungi Sales Kami'}
                  </BtnLinkPrimary>
                  {/* <BtnLinkPrimary
                    tw="w-full text-center flex items-center justify-center"
                    className="gtm-call-button"
                    to={
                      '/hubungi-kami-via-whatsapp'
                    }
                    target={'__blank'}
                  >
                    {'Hubungi Sales Kami'}
                  </BtnLinkPrimary> */}
                </div>
              </div>

              <div tw="my-4 mx-4 lg:mx-0 dark:text-white text-b1">
                {documentToReactComponents(description, descriptionOptions)}
              </div>

              <div
                tw="bg-blue-sky p-4"
                css={css`
                  ${!recommendation ? tw`hidden` : ''}
                `}
              >
                <div tw="mb-3">Rekomendasi:</div>
                {documentToReactComponents(recommendation, blockOptions)}
                <div tw="mt-4">
                  {/* <BtnLinkPrimary
                    tw="text-center mr-2"
                    className="gtm-call-button"
                    to={`${pageData?.recommendationLink}`}
                    css={css`
                      ${!pageData?.recommendationLink ? tw`hidden` : ''}
                      @media (max-width: 1024px) {
                        ${tw`w-full`}
                        margin-bottom: 1rem;
                      }
                    `}
                  >
                    Pelajari Selengkapnya
                  </BtnLinkPrimary> */}
                  <BtnLinkPrimary
                    tw="text-center mr-2"
                    className="gtm-call-button"
                    to={pageData?.recommendationLink || '/hubungi-kami-via-whatsapp'}
                    target={pageData?.recommendationLink ? null : '__blank'}
                  >
                    {pageData?.recommendationText || 'Hubungi Sales Kami'}
                  </BtnLinkPrimary>
                  <AnchorLink
                    className="gtm-alternative-package"
                    tw="text-blue-lapis font-gilroy font-extrabold block md:inline lg:inline mt-4 md:mt-0 lg:mt-0"
                    to={`${pageData?.paketLainnyaLink || '/#layanan-dan-harga'}`}
                  >
                    {pageData?.paketLainnyaText || 'Lihat Paket Lainnya'}
                  </AnchorLink>
                </div>
              </div>

              <div tw="mx-4 lg:mx-0">
                <Partner
                  title={
                    <div tw="text-b1 text-black-300 dark:text-black-700 text-left lg:text-center py-6 m-0">
                      Easybiz sudah dipercaya oleh 3000+ Perusahaan di Indonesia
                    </div>
                  }
                  noMargin={true}
                ></Partner>

                <div>
                  <span tw="dark:text-white">Tag: </span>
                  <div tw="mt-3 flex flex-wrap mb-3">
                    {tags?.map((item, i) => (
                      <Link
                        className="gtm-article-tag"
                        key={'tags' + i}
                        tw="bg-black-700 rounded-lg py-2 px-2 mr-2 mb-3 cursor-pointer no-underline text-black-100 visited:text-black-100"
                        to={`/tag/${
                          item.replace(new RegExp(' ', 'g'), '-').toLowerCase() + '/' ?? ''
                        }`}
                      >
                        {item}
                      </Link>
                    ))}
                  </div>
                </div>

                <div>
                  <span tw="dark:text-white">Bagikan artikel ini</span>
                  <div tw="mt-3 flex flex-wrap mb-3">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${canonical}`}
                      target="__blank"
                      aria-label="Share ke Facebook"
                      tw="text-white rounded-lg p-2 flex items-center mr-2 mb-2"
                      css={css`
                        background-color: #1877f2;
                        text-decoration: none;
                      `}
                    >
                      <span className="icon icon-facebook"></span>
                      <div tw="ml-2">Facebook</div>
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${canonical}`}
                      target="__blank"
                      aria-label="Share ke LinkedIn"
                      tw="text-white rounded-lg p-2 flex items-center mr-2 mb-2"
                      css={css`
                        background-color: #2867b2;
                        text-decoration: none;
                      `}
                    >
                      <span className="icon icon-linkedin"></span>
                      <div tw="ml-2">LinkedIn</div>
                    </a>
                    <a
                      href={`http://twitter.com/share?text=${pageData.title}&url=${canonical}`}
                      target="__blank"
                      aria-label="Share ke Twitter"
                      tw="text-white rounded-lg p-2 flex items-center mr-2 mb-2"
                      css={css`
                        background-color: #1d9bf0;
                        text-decoration: none;
                      `}
                    >
                      <span className="icon icon-twitter"></span>
                      <div tw="ml-2">Twitter</div>
                    </a>
                    <a
                      href={`https://api.whatsapp.com/send?text=${pageData?.title}%20|%20${canonical}`}
                      target="__blank"
                      aria-label="Share ke Whatsapp"
                      tw="text-white rounded-lg p-2 flex items-center mr-2 mb-2"
                      css={css`
                        background-color: #25d366;
                        text-decoration: none;
                      `}
                    >
                      <span className="icon icon-whatsapp"></span>
                      <div tw="ml-2">Whatsapp</div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section tw="bg-white dark:bg-black-200">
        <div tw="container">
          <div tw="mx-4 lg:mx-32 py-12">
            <p tw="text-h2 text-black-100 dark:text-white pb-4 m-0">
              Artikel yang Cocok untuk Anda
            </p>
            <div tw="grid grid-cols-1 lg:grid-cols-3 lg:gap-4 py-4">
              {relatedArticles?.map((item, i) => (
                <ArticleItem
                  className="gtm-related-article"
                  key={'article' + i}
                  title={item.title}
                  date={item.date}
                  info={item.info}
                  img={item.img?.fluid}
                  link={item.uid}
                  category={item.category}
                >
                  {documentToReactComponents(JSON.parse(item.shortDescription?.raw))}
                </ArticleItem>
              ))}
            </div>
          </div>
        </div>
      </section>
    </BaseLayout>
  );
};

export default ArtikelDetail;

export const query = graphql`
  query getArtikelDetailData($uid: String!) {
    allContentfulArtikel(filter: { uid: { eq: $uid } }) {
      nodes {
        uid
      }
      edges {
        node {
          seo {
            title
            description {
              description
            }
            keyword
            language
            robots
            author
            publisher
            updatedAt
            oglocale
            ogtype
          }
          updatedAt
          createdAt
          uid
          title
          description {
            raw
          }
          recommendation {
            raw
          }
          recommendationLink
          recommendationText
          paketLainnyaLink
          paketLainnyaText
          shortDescription {
            raw
          }
          summary {
            raw
          }
          summaryButtonText
          summaryButtonLink
          author
          info
          date
          category
          tags
          relatedArticle {
            uid
            title
            category
            date
            info
            shortDescription {
              raw
            }
            img {
              file {
                url
                fileName
              }
              fluid(maxHeight: 280, maxWidth: 500) {
                ...ContentfulImageFluidFields
              }
            }
          }
          img {
            file {
              url
              fileName
            }
            fluid(maxHeight: 280, maxWidth: 500) {
              ...ContentfulImageFluidFields
            }
          }
          imageAlt
        }
      }
    }
    logo: file(relativePath: { eq: "images/logo.png" }) {
      childrenImageSharp {
        fluid(maxWidth: 130) {
          ...FileImageSharpFluid
        }
      }
    }
    site {
      siteMetadata {
        ...SiteMetadataFields
      }
    }
  }
`;
